/**
 * 成果物サムネイル URL を Cloudflare Image Transformations 経由の縮小版へ書き換えるユーティリティ。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 成果物サムネイルの公開 URL を配信時リサイズ付き URL へ変換する
 * @graph-connects none
 */

/**
 * 配信時リサイズを適用する成果物サムネイルの origin（R2 の Custom Domain）。
 *
 * この origin だけを対象にし、drive.google.com などそれ以外の URL は素通しする。
 * `/cdn-cgi/image/` は Cloudflare zone の機能なので、zone 外のホストに付けてもただの 404 になる。
 * @graph-connects none
 */
const THUMBNAIL_ORIGIN = 'https://design-thumbnails.air-closet.ai';

/**
 * Cloudflare の予約パス。ここから始まる URL は既に変換済み（または別の cdn-cgi 機能）なので触らない。
 * 変換を二重に付けると Cloudflare がパスを解決できず 404 になる。
 * @graph-connects none
 */
const CDN_CGI_PREFIX = `${THUMBNAIL_ORIGIN}/cdn-cgi/`;

/**
 * 変換オプション。カードは `aspect-video` の枠に `object-contain` で描画され、3 カラム時の実寸は
 * 350px 前後。DPR2 の端末を考慮して 640px に収める。
 *
 * - `fit=scale-down`: 指定幅より小さい画像は拡大しない（元より汚くしない）
 * - `format=auto`: 対応ブラウザへ WebP / AVIF を返す
 * - `quality=75`: サムネイル用途で劣化が目立たない範囲の既定値
 * @graph-connects none
 */
const TRANSFORM_OPTIONS = 'width=640,quality=75,format=auto,fit=scale-down';

/**
 * 成果物サムネイルの公開 URL を `/cdn-cgi/image/...` 付きの縮小版 URL に変換する。
 *
 * 対象 origin 以外・既に cdn-cgi 配下の URL はそのまま返す。null / 空文字は null を返し、
 * 呼び出し側のフォールバックに委ねる。URL のパースは行わず既知 origin の前方一致だけで判定するため、
 * 相対パスや壊れた値が来ても例外にならず素通しになる。
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 成果物サムネイル URL を配信時リサイズ付き URL へ変換する
 * @graph-connects none
 */
export function toResizedThumbnailUrl(rawUrl: string | null | undefined): string | null {
  if (rawUrl === null || rawUrl === undefined || rawUrl === '') {
    return null;
  }
  if (rawUrl.startsWith(CDN_CGI_PREFIX)) {
    return rawUrl;
  }

  const originPrefix = `${THUMBNAIL_ORIGIN}/`;
  if (!rawUrl.startsWith(originPrefix)) {
    return rawUrl;
  }

  const objectPath = rawUrl.slice(originPrefix.length);
  if (objectPath === '') {
    return rawUrl;
  }
  return `${THUMBNAIL_ORIGIN}/cdn-cgi/image/${TRANSFORM_OPTIONS}/${objectPath}`;
}
